import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Calendar, Plus, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { ChatHistoryList } from '@/components/panels/ChatHistoryList.js';
import { SchedulesList } from '@/components/panels/SchedulesList.js';
import { CreateScheduleModal } from '@/components/scheduler/CreateScheduleModal.js';
import { useServices } from '@/contexts/ServicesContext.js';
import { useSchedulerStore } from '@/stores/schedulerStore.js';
import { cn } from '@/lib/utils.js';

type DrawerSection = 'chats' | 'routines';

interface MobileDrawerContentProps {
  onSelect: () => void;
  onClose: () => void;
  onViewScheduleDetails: (scheduleId: string) => void;
}

export function MobileDrawerContent({
  onSelect,
  onClose,
  onViewScheduleDetails,
}: MobileDrawerContentProps) {
  const [activeSection, setActiveSection] = useState<DrawerSection>('chats');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const navigate = useNavigate();
  const { sessionService } = useServices();
  const runningCount = useSchedulerStore((state) => state.getRunningCount());

  const handleNewConversation = () => {
    sessionService.returnToWelcome();
    navigate('/');
    onSelect();
  };

  const handleNewRoutine = () => {
    setShowCreateModal(true);
  };

  const handleViewDetails = (scheduleId: string) => {
    onViewScheduleDetails(scheduleId);
    onClose();
  };

  const sections: Array<{
    id: DrawerSection;
    label: string;
    icon: typeof MessageSquare;
    badge?: number | null;
  }> = [
    {
      id: 'chats',
      label: 'Chats',
      icon: MessageSquare,
    },
    {
      id: 'routines',
      label: 'Routines',
      icon: Calendar,
      badge: runningCount > 0 ? runningCount : null,
    },
  ];

  return (
    <>
      <div className="flex flex-col h-full">
        {/* Header with section tabs */}
        <div className="border-b border-border/50">
          {/* Top bar with close and add buttons */}
          <div className="flex items-center justify-between px-4 py-3">
            <h1 className="text-lg font-semibold">
              {activeSection === 'chats' ? 'Chats' : 'Routines'}
            </h1>
            <div className="flex items-center gap-2">
              <Button
                size="icon"
                variant="default"
                onClick={activeSection === 'chats' ? handleNewConversation : handleNewRoutine}
                className="rounded-lg bg-primary hover:bg-primary/90"
              >
                <Plus className="h-5 w-5" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                onClick={onClose}
                className="rounded"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Section tabs */}
          <div className="flex px-4 pb-2 gap-2">
            {sections.map((section) => {
              const Icon = section.icon;
              const isActive = activeSection === section.id;

              return (
                <button
                  key={section.id}
                  onClick={() => {
                    setActiveSection(section.id);
                    setSearchQuery('');
                  }}
                  className={cn(
                    'flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {section.label}
                  {section.badge && (
                    <span className="flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-yellow-500 text-[10px] font-bold text-black">
                      {section.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Search */}
        <div className="px-4 py-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={
                activeSection === 'chats'
                  ? 'Search conversations...'
                  : 'Search routines...'
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-10 bg-muted/50 border-0"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {activeSection === 'chats' ? (
            <ChatHistoryList searchQuery={searchQuery} onSelect={onSelect} />
          ) : (
            <SchedulesList
              searchQuery={searchQuery}
              onViewDetails={handleViewDetails}
            />
          )}
        </div>
      </div>

      {showCreateModal && (
        <CreateScheduleModal onClose={() => setShowCreateModal(false)} />
      )}
    </>
  );
}
